<template>
  <div class="bg-light min-vh-100">
    <main class="container py-4">
      <div class="row justify-content-center">
        <div class="col-lg-12">
          <h1 class="h4 mb-3">Dashboard</h1>

          <div v-if="loading" class="text-center py-5">
            <div class="spinner-border" role="status"></div>
          </div>

          <div v-else-if="error" class="alert alert-danger py-2">{{ error }}</div>

          <div v-else>
            <!-- Margin Alerts -->
            <div v-if="isSupervisor && data.margin_alerts && data.margin_alerts.length" class="mb-3">
              <div
                v-for="alert in data.margin_alerts"
                :key="alert.device_commonname"
                class="alert alert-warning py-2 mb-2"
              >
                <strong>{{ alert.device_commonname }}</strong>: avg margin
                ${{ Number(alert.avg_margin).toFixed(2) }} over {{ alert.units }} units (last 30 days).
                Below $50 threshold.
              </div>
            </div>

            <!-- KPI Cards -->
            <div class="row g-3 mb-4">
              <div class="col-md col-6">
                <div class="card text-center shadow-sm clickable-card" role="button" @click="drillDown({})">
                  <div class="card-body py-3">
                    <div class="h3 mb-0">{{ data.total_open_orders }}</div>
                    <small class="text-muted">Open Devices</small>
                  </div>
                </div>
              </div>
              <div class="col-md col-6">
                <div class="card text-center shadow-sm clickable-card" role="button" @click="drillDown({ status: 'HOLD' })">
                  <div class="card-body py-3">
                    <div class="h3 mb-0 text-warning">{{ data.orders_on_hold }}</div>
                    <small class="text-muted">On Hold</small>
                  </div>
                </div>
              </div>
              <div class="col-md col-6">
                <div class="card text-center shadow-sm clickable-card" role="button" @click="drillDown({ status: 'COMPLETE' })">
                  <div class="card-body py-3">
                    <div class="h3 mb-0 text-success">{{ data.completed_this_week }}</div>
                    <small class="text-muted">Completed This Week</small>
                  </div>
                </div>
              </div>
              <div class="col-md col-6">
                <div class="card text-center shadow-sm clickable-card" role="button" @click="drillDown({ status: 'SOLD' })">
                  <div class="card-body py-3">
                    <div class="h3 mb-0">{{ data.sold_this_week }}</div>
                    <small class="text-muted">Sold This Week</small>
                  </div>
                </div>
              </div>
              <div class="col-md col-6" v-if="isSupervisor">
                <div class="card text-center shadow-sm">
                  <div class="card-body py-3">
                    <div class="h3 mb-0">${{ formatMoney(data.revenue_this_month) }}</div>
                    <small class="text-muted">Revenue This Month</small>
                  </div>
                </div>
              </div>
            </div>

            <!-- Shipping KPIs -->
            <div class="row g-3 mb-4" v-if="data.shipping_summary">
              <div class="col-md col-6">
                <div class="card text-center shadow-sm clickable-card" role="button" @click="drillDown({ shipping_status: 'IN_TRANSIT' })">
                  <div class="card-body py-3">
                    <div class="h3 mb-0 text-info">{{ data.shipping_summary.in_transit }}</div>
                    <small class="text-muted">In Transit</small>
                  </div>
                </div>
              </div>
              <div class="col-md col-6">
                <div class="card text-center shadow-sm clickable-card" role="button" @click="drillDown({ shipping_status: 'DELIVERED' })">
                  <div class="card-body py-3">
                    <div class="h3 mb-0 text-success">{{ data.shipping_summary.delivered }}</div>
                    <small class="text-muted">Delivered</small>
                  </div>
                </div>
              </div>
              <div class="col-md col-6">
                <div class="card text-center shadow-sm clickable-card" role="button" @click="drillDown({ shipping_status: 'EXCEPTION' })">
                  <div class="card-body py-3">
                    <div class="h3 mb-0 text-danger">{{ data.shipping_summary.exceptions }}</div>
                    <small class="text-muted">Exceptions</small>
                  </div>
                </div>
              </div>
            </div>

            <!-- Quick links -->
            <div class="mb-4 d-flex gap-2">
              <button class="btn btn-primary btn-sm" @click="$router.push({ name: 'CreateOrder' })">New Device</button>
              <button class="btn btn-outline-primary btn-sm" @click="$router.push({ name: 'BatchCreate' })">Batch Create</button>
              <button class="btn btn-outline-secondary btn-sm" @click="$router.push({ name: 'IssuesList' })">View Issues</button>
            </div>

            <div class="row g-4">
              <!-- Orders by Status -->
              <div class="col-lg-6">
                <div class="card shadow-sm">
                  <div class="card-body">
                    <h2 class="h6 mb-2">Devices by Status</h2>
                    <table class="table table-sm mb-0" v-if="data.orders_by_status && data.orders_by_status.length">
                      <thead class="table-light">
                        <tr>
                          <th>Status</th>
                          <th class="text-end">Count</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="row in data.orders_by_status" :key="row.order_status" class="clickable-row" role="button" @click="drillDown({ status: row.order_status })">
                          <td>{{ row.order_status }}</td>
                          <td class="text-end">{{ row.count }}</td>
                        </tr>
                      </tbody>
                    </table>
                    <div v-else class="text-muted small">No devices yet.</div>
                  </div>
                </div>
              </div>

              <!-- Recent Activity -->
              <div class="col-lg-6">
                <div class="card shadow-sm">
                  <div class="card-body">
                    <h2 class="h6 mb-2">Recent Activity</h2>
                    <ul class="list-group list-group-flush" v-if="data.recent_activity && data.recent_activity.length">
                      <li v-for="log in data.recent_activity" :key="log.id" class="list-group-item px-0 py-1 small">
                        <span class="fw-semibold">{{ log.user__username || "System" }}</span>
                        {{ log.action }}
                        <span class="text-muted">{{ log.resource_type }}#{{ log.resource_id }}</span>
                        <br />
                        <small class="text-muted">{{ new Date(log.created_at).toLocaleString() }}</small>
                      </li>
                    </ul>
                    <div v-else class="text-muted small">No recent activity.</div>
                  </div>
                </div>
              </div>
            </div>

            <!-- My Assigned Orders (technician only) -->
            <div class="mt-4" v-if="data.my_orders">
              <div class="card shadow-sm">
                <div class="card-body">
                  <h2 class="h6 mb-2">My Assigned Devices</h2>
                  <div v-if="data.my_orders.length === 0" class="text-muted small">No open devices assigned to you.</div>
                  <table v-else class="table table-sm mb-0">
                    <thead class="table-light">
                      <tr>
                        <th>Order #</th>
                        <th>Device</th>
                        <th>Serial</th>
                        <th>Template</th>
                        <th>Status</th>
                        <th class="text-end">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="o in data.my_orders" :key="o.order_id">
                        <td>{{ o.order_id }}</td>
                        <td>{{ o.device_commonname }}</td>
                        <td>{{ o.serial_number }}</td>
                        <td>{{ o.template_name }}</td>
                        <td>{{ o.order_status }}</td>
                        <td class="text-end">
                          <button class="btn btn-outline-secondary btn-sm" @click="$router.push({ name: 'OrderDetail', params: { id: o.order_id } })">
                            Open
                          </button>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  </div>
</template>

<script>
import { APIService } from "../http/APIService";

export default {
  name: "DashboardView",
  data() {
    return {
      data: {},
      loading: false,
      error: null,
      api: new APIService(),
    };
  },
  computed: {
    isSupervisor() {
      try {
        const raw = localStorage.getItem("currentUser");
        if (!raw) return false;
        const user = JSON.parse(raw);
        const role = (user.role || "").toLowerCase();
        return role === "supervisor" || role === "admin";
      } catch {
        return false;
      }
    },
  },
  created() {
    this.fetchDashboard();
  },
  methods: {
    async fetchDashboard() {
      this.loading = true;
      this.error = null;
      try {
        const accessToken = localStorage.getItem("accessToken");
        const res = await this.api.getDashboard(accessToken);
        this.data = res.data || {};
      } catch {
        this.error = "Unable to load dashboard.";
      } finally {
        this.loading = false;
      }
    },
    formatMoney(val) {
      if (!val && val !== 0) return "0.00";
      return Number(val).toFixed(2);
    },
    drillDown(query) {
      this.$router.push({ name: "OrdersList", query });
    },
  },
};
</script>

<style scoped>
.clickable-card {
  cursor: pointer;
  transition: transform 0.1s, box-shadow 0.1s;
}
.clickable-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 .25rem .75rem rgba(0,0,0,.15) !important;
}
.clickable-row {
  cursor: pointer;
}
.clickable-row:hover {
  background-color: rgba(0,0,0,.04);
}
</style>
